import XIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { DialogProps } from "../types"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import ControlledCheckBox from "@/components/controlledInputs/ControlledCheckBox"
import useCreateAdvertisement from "@/hooks/api/advertisements/useCreateAdvertisement"
import { FileWithPath } from "react-dropzone"
import { useState } from "react"
import AdvertisementFileUpload from "@/components/advertisements/AdvertisementFileUpload"
import { PutObjectCommand } from "@aws-sdk/client-s3"
import { s3, warmS3Credentials } from "@/common/globals"
import { useSnackbar } from "@/contexts/SnackbarContext"

const createAdvertisementSchema = z.object({
  company_name: z.string(),
  action_link: z.string(),
  active: z.boolean(),
  advertisement_images: z.object({
    large_image: z.string().optional(),
    small_image: z.string().optional(),
    mobile_image: z.string().optional(),
    description: z.string(),
  }),
})
export type CreateAdvertisementFormDataType = z.infer<
  typeof createAdvertisementSchema
>

const defaultValues: CreateAdvertisementFormDataType = {
  company_name: "",
  action_link: "",
  active: false,
  advertisement_images: {
    description: "",
  },
}

interface CreateAdvertisementDialogProps extends DialogProps {}

export default function CreateAdvertisementDialog({
  isOpen,
  closeDialog,
}: CreateAdvertisementDialogProps) {
  const { mutateAsync: createAdvertisement } = useCreateAdvertisement()
  function handleClose() {
    closeDialog()
  }

  const { showSnackbar } = useSnackbar()

  const [largeImage, setLargeImage] = useState<FileWithPath>()
  const [smallImage, setSmallImage] = useState<FileWithPath>()
  const [mobileImage, setMobileImage] = useState<FileWithPath>()

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(createAdvertisementSchema),
    defaultValues,
  })

  const onSubmit = async (data: CreateAdvertisementFormDataType) => {
    data.advertisement_images = {
      large_image: largeImage?.path,
      small_image: smallImage?.path,
      mobile_image: mobileImage?.path,
      description: data.advertisement_images.description,
    }

    // Ensure S3 credentials are ready before uploading
    await warmS3Credentials()

    createAdvertisement(data).then(async (res) => {
      const advertisement = res.data

      if (largeImage) {
        const largeImageParams = {
          Bucket: `${import.meta.env.VITE_AWS_ADVERTISEMENT_BUCKET}`,
          Key: `${advertisement.insertId}/${largeImage?.name}`,
          Body: largeImage,
          ContentType: largeImage.type,
        }

        try {
          await s3.send(new PutObjectCommand(largeImageParams))
        } catch (err) {
          showSnackbar("Could not upload image", "error")
          console.error("There was a problem uploading images", err)
        }
      }
      if (smallImage) {
        const smallImageParams = {
          Bucket: `${import.meta.env.VITE_AWS_ADVERTISEMENT_BUCKET}`,
          Key: `${advertisement.insertId}/${smallImage?.name}`,
          Body: smallImage,
          ContentType: smallImage.type,
        }

        try {
          await s3.send(new PutObjectCommand(smallImageParams))
        } catch (err) {
          showSnackbar("Could not upload image", "error")
          console.error("There was a problem uploading images", err)
        }
      }
      if (mobileImage) {
        const mobileImageParams = {
          Bucket: `${import.meta.env.VITE_AWS_ADVERTISEMENT_BUCKET}`,
          Key: `${advertisement.insertId}/${mobileImage?.name}`,
          Body: mobileImage,
          ContentType: mobileImage.type,
        }

        try {
          await s3.send(new PutObjectCommand(mobileImageParams))
        } catch (err) {
          showSnackbar("Could not upload image", "error")
          console.error("There was a problem uploading images", err)
        }
      }
      showSnackbar("Advertisement Created", "success")
    })
    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth={"500px"}
        pl={3}
        pr={1}
        py={1}
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography>Create Advertisement</Typography>
        <Box>
          <IconButton color="inherit" onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent
        sx={{
          py: 2,
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="company_name"
            label="Company Name"
            type="text"
          />
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="action_link"
            label="Action Link"
            type="text"
          />
          <ControlledCheckBox control={control} name="active" label="Active" />
          <Divider sx={{ paddingTop: 2 }} />
          <AdvertisementFileUpload
            largeImage={largeImage}
            setLargeImage={setLargeImage}
            smallImage={smallImage}
            setSmallImage={setSmallImage}
            mobileImage={mobileImage}
            setMobileImage={setMobileImage}
          />
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="advertisement_images.description"
            label="Description"
            type="text"
          />
          <Divider sx={{ paddingTop: 2 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mt={5}
          >
            <Button variant="text" color="inherit" onClick={handleClose}>
              Cancel
            </Button>

            <Button variant="contained" color="primary" type="submit">
              Send
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
